import React, { useState } from 'react';
import { ShieldCheck, Mail, Phone, Globe, Lock, Landmark, CheckCircle, ArrowRight, MessageSquare, Linkedin, Upload, Briefcase, TrendingUp, HandHelping } from 'lucide-react';
import { CONTACT_EMAIL, CONTACT_PHONE } from '../constants';

const TrustPage: React.FC = () => {
  const [formStatus, setFormStatus] = useState<'idle' | 'sending' | 'success'>('idle');

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setFormStatus('sending');
    setTimeout(() => setFormStatus('success'), 1500);
  };

  return (
    <div className="pt-32 pb-20 overflow-hidden">
      {/* TRUST SECTION */}
      <section className="px-6 max-w-7xl mx-auto mb-40 relative">
        <div className="absolute top-0 right-0 w-[500px] h-[500px] bg-brand-blue/10 blur-[150px] rounded-full pointer-events-none"></div>
        <div className="grid lg:grid-cols-2 gap-20 items-center">
          <div className="space-y-10 parallax-layer" data-speed="0.1">
            <div className="inline-flex items-center space-x-3 px-4 py-2 glass border-brand-blue/30 rounded-full">
              <span className="text-[10px] uppercase tracking-[0.4em] text-brand-blue font-bold">Foundation of Governance</span>
            </div>
            <h2 className="text-5xl md:text-[6.5rem] font-serif tracking-tighter leading-[0.9] text-white">
              Built for <span className="text-brand-blue">Trust.</span>
            </h2>
            <p className="text-2xl text-white/50 font-light leading-relaxed">
              Designed for governments and key institutions where security and integrity are not optional.
            </p>
            
            <div className="space-y-6 pt-6">
              {[
                { 
                  icon: <Lock size={20} />, 
                  title: 'Secure Architecture', 
                  desc: 'Verified identity access preventing bot manipulation and ensuring data sovereignty.' 
                },
                { 
                  icon: <Landmark size={20} />, 
                  title: 'Tested Leadership', 
                  desc: 'Developed and tested alongside leaders in government and academic institutions.' 
                },
                { 
                  icon: <CheckCircle size={20} />, 
                  title: 'Full Compliance', 
                  desc: 'Aligned with global transparency, accessibility, and high-security data standards.' 
                }
              ].map((item, i) => (
                <div key={i} className="flex items-start space-x-6 group hover:translate-x-4 transition-all duration-500 cursor-default">
                   <div className="mt-1 w-12 h-12 glass rounded-xl flex items-center justify-center text-brand-blue group-hover:bg-brand-blue group-hover:text-white transition-all shadow-[0_0_15px_rgba(86,190,249,0.1)] group-hover:shadow-brand-blue/30">
                      {item.icon}
                   </div>
                   <div className="space-y-2">
                      <h4 className="text-lg font-bold italic tracking-tight">{item.title}</h4>
                      <p className="text-sm text-white/40 max-w-md">{item.desc}</p>
                   </div>
                </div>
              ))}
            </div>
          </div>
          
          <div className="relative parallax-layer" data-speed="-0.05">
             <div className="glass p-1 rounded-[3rem] border-white/10 relative overflow-hidden shadow-2xl group hover:shadow-brand-blue/20 transition-all duration-1000">
                <div className="absolute inset-0 bg-brand-blue/[0.03]"></div>
                <div className="relative z-10 p-12 space-y-12">
                   <div className="text-center">
                      <ShieldCheck size={80} className="text-brand-blue mx-auto mb-6 group-hover:scale-110 transition-transform duration-700" />
                      <span className="text-[10px] uppercase tracking-[0.3em] font-bold text-white/30">Verified Governance Protocol</span>
                   </div>
                   <div className="space-y-4">
                      {[1,2,3,4].map(i => (
                        <div key={i} className="h-1.5 w-full bg-white/5 rounded-full overflow-hidden">
                          <div className="h-full bg-brand-blue/40" style={{ width: `${40 + (i * 15)}%` }}></div>
                        </div>
                      ))}
                   </div>
                   <p className="text-center text-[10px] uppercase tracking-widest text-white/20 italic">Encrypted End-to-End Environment</p>
                </div>
             </div>
          </div>
        </div>
      </section>

      {/* PARTNERS & INQUIRY FORM */}
      <section className="py-40 px-6 bg-[#0c0c0d] border-y border-white/5 relative overflow-hidden">
        <div className="max-w-7xl mx-auto grid lg:grid-cols-2 gap-20">
          <div className="space-y-12">
             <h2 className="text-xs uppercase tracking-[0.4em] text-brand-blue font-bold">Collaboration</h2>
             <h3 className="text-4xl md:text-6xl font-serif tracking-tighter text-white">Built with partners.</h3>
             <p className="text-xl text-white font-bold leading-relaxed opacity-80">
               We help you secure high-value contracts and expand your deployment into new global markets.
             </p>
             
             <div className="space-y-8 pt-8">
               <div className="flex items-start space-x-6 group hover:translate-x-2 transition-all">
                 <HandHelping className="text-brand-blue mt-1 group-hover:scale-125 transition-transform" size={24} />
                 <div>
                    <h4 className="text-lg font-bold mb-2 uppercase tracking-wide">Contract Value Sharing</h4>
                    <p className="text-sm text-white/40 font-medium leading-relaxed">Boulai actively works to secure major governance and infrastructure contracts. We allocate a transparent percentage of the contract value directly to our partners, ensuring mutual success and high-regard resource planning.</p>
                 </div>
               </div>
               <div className="flex items-start space-x-6 group hover:translate-x-2 transition-all">
                 <TrendingUp className="text-brand-blue mt-1 group-hover:scale-125 transition-transform" size={24} />
                 <div>
                    <h4 className="text-lg font-bold mb-2 uppercase tracking-wide">Accelerate New Business</h4>
                    <p className="text-sm text-white/40 font-medium leading-relaxed">If you build mobility, ERS, or IoT, we co-deploy to expand your business footprint. Boulai opens doors to municipalities where you may not currently be deployed, leading with intelligence so you can lead with infrastructure.</p>
                 </div>
               </div>
             </div>
          </div>

          <div className="glass p-10 md:p-12 rounded-[3rem] border-white/10 bg-white/[0.01] relative shadow-2xl hover:shadow-brand-blue/10 transition-all duration-700">
            <h4 className="text-2xl font-bold italic mb-8">Partner Inquiry</h4>
            {formStatus === 'success' ? (
              <div className="text-center py-20 space-y-6">
                 <div className="w-16 h-16 bg-green-500/20 text-green-500 rounded-full flex items-center justify-center mx-auto mb-6">
                   <CheckCircle size={32} />
                 </div>
                 <h5 className="text-xl font-bold">Inquiry Received</h5>
                 <p className="text-white/40">Our partnership team will review your details and reach out within 48 hours.</p>
                 <button onClick={() => setFormStatus('idle')} className="text-brand-blue text-xs uppercase tracking-widest font-bold hover:underline">Send another</button>
              </div>
            ) : (
              <form onSubmit={handleSubmit} className="space-y-6">
                <div className="grid md:grid-cols-2 gap-6">
                  <div className="space-y-2">
                    <label className="text-[10px] uppercase tracking-widest font-bold text-white/30">Full Name</label>
                    <input required type="text" className="w-full bg-white/5 border border-white/10 rounded-xl px-4 py-3 text-sm focus:outline-none focus:border-brand-blue transition-colors text-white" placeholder="John Doe" />
                  </div>
                  <div className="space-y-2">
                    <label className="text-[10px] uppercase tracking-widest font-bold text-white/30">Contact (Email or Phone)</label>
                    <input required type="text" className="w-full bg-white/5 border border-white/10 rounded-xl px-4 py-3 text-sm focus:outline-none focus:border-brand-blue transition-colors text-white" placeholder="john@example.com" />
                  </div>
                </div>
                <div className="space-y-2">
                  <label className="text-[10px] uppercase tracking-widest font-bold text-white/30">Company Website</label>
                  <input type="url" className="w-full bg-white/5 border border-white/10 rounded-xl px-4 py-3 text-sm focus:outline-none focus:border-brand-blue transition-colors text-white" placeholder="https://example.com" />
                </div>
                <div className="space-y-2">
                  <label className="text-[10px] uppercase tracking-widest font-bold text-white/30">Attach Document (Optional)</label>
                  <div className="relative group">
                    <input type="file" className="absolute inset-0 w-full h-full opacity-0 cursor-pointer z-20" />
                    <div className="w-full bg-white/5 border border-dashed border-white/20 rounded-xl px-4 py-6 text-center group-hover:border-brand-blue transition-colors">
                      <Upload className="mx-auto text-white/20 mb-2 group-hover:text-brand-blue transition-colors" size={20} />
                      <span className="text-xs text-white/30">Click to upload or drag & drop</span>
                    </div>
                  </div>
                </div>
                <button 
                  type="submit" 
                  disabled={formStatus === 'sending'}
                  className="w-full py-5 bg-brand-blue text-white font-black uppercase text-xs tracking-[0.2em] rounded-xl hover:bg-white hover:text-black transition-all shadow-xl shadow-brand-blue/20 flex items-center justify-center space-x-3 active:scale-95"
                >
                  {formStatus === 'sending' ? 'Sending...' : 'Submit Inquiry'}
                  <ArrowRight size={16} />
                </button>
              </form>
            )}
          </div>
        </div>
      </section>

      {/* CONTACT SECTION */}
      <section id="contact-details" className="py-40 px-6 max-w-7xl mx-auto">
        <div className="grid lg:grid-cols-2 gap-20 items-center">
          <div className="space-y-12 parallax-layer" data-speed="0.1">
            <div>
              <h2 className="text-xs uppercase tracking-[0.4em] text-brand-blue font-bold mb-8">Direct Contact</h2>
              <h3 className="text-4xl md:text-5xl font-serif leading-tight mb-8 text-white">
                Ready to transform public sentiment?
              </h3>
            </div>
            
            <div className="space-y-10">
               <a href={`mailto:${CONTACT_EMAIL}`} className="flex items-center space-x-6 group">
                  <div className="w-16 h-16 glass rounded-2xl flex items-center justify-center text-brand-blue group-hover:bg-brand-blue group-hover:text-white transition-all transform group-hover:rotate-6">
                    <Mail size={24} />
                  </div>
                  <div>
                    <span className="block text-[10px] uppercase tracking-[0.3em] text-white/30 font-bold mb-1">Email mdahhan</span>
                    <span className="text-2xl md:text-3xl font-bold italic group-hover:text-brand-blue transition-colors">{CONTACT_EMAIL}</span>
                  </div>
               </a>
               
               <a href={`tel:${CONTACT_PHONE.replace(/-/g, '')}`} className="flex items-center space-x-6 group">
                  <div className="w-16 h-16 glass rounded-2xl flex items-center justify-center text-brand-blue group-hover:bg-brand-blue group-hover:text-white transition-all transform group-hover:rotate-6">
                    <Phone size={24} />
                  </div>
                  <div>
                    <span className="block text-[10px] uppercase tracking-[0.3em] text-white/30 font-bold mb-1">Direct Line</span>
                    <span className="text-2xl md:text-3xl font-bold italic group-hover:text-brand-blue transition-colors">{CONTACT_PHONE}</span>
                  </div>
               </a>
            </div>
          </div>

          <div className="glass p-12 md:p-16 rounded-[4rem] border-white/5 bg-brand-blue/[0.02] space-y-10 relative overflow-hidden group animate-float shadow-3xl">
             <div className="absolute top-0 right-0 p-10 opacity-5 group-hover:scale-125 transition-transform duration-1000">
                <Globe size={300} />
             </div>
             
             <div className="relative z-10 space-y-8">
                <h4 className="text-2xl font-bold italic">Schedule a walkthrough.</h4>
                <p className="text-white/40 leading-relaxed font-medium">
                  Join global leaders who are transforming public sentiment into actionable policy. Request a walkthrough of our core modules.
                </p>
                
                <div className="flex flex-col gap-4">
                  <a 
                    href={`mailto:${CONTACT_EMAIL}?subject=Book a Demo`}
                    className="w-full py-6 bg-white text-black text-center font-black uppercase text-xs tracking-widest rounded-2xl hover:bg-brand-blue hover:text-white transition-all transform active:scale-95 shadow-xl hover:shadow-brand-blue/30"
                  >
                    Book a Demo
                  </a>
                  <div className="flex justify-center space-x-8 opacity-40 mt-6">
                     <Linkedin size={24} className="hover:text-brand-blue cursor-pointer transition-colors" />
                     <MessageSquare size={24} className="hover:text-brand-blue cursor-pointer transition-colors" />
                  </div>
                </div>
             </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default TrustPage;